import { Check } from 'lucide-react';
import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import type { SelectOption } from './Select.tsx';
import { stepFrom, typedMatch } from './select-choice.ts';
import styles from './Select.module.css';

export type SelectPopupProps = {
  id: string;
  label: string;
  options: readonly SelectOption[];
  value: string;
  place: CSSProperties;
  onPick: (value: string) => void;
  onClose: () => void;
};

const KEY_STEPS: Readonly<Record<string, (options: readonly SelectOption[], active: number) => number>> = {
  ArrowDown: (options, active) => stepFrom(options, active, 1),
  ArrowUp: (options, active) => stepFrom(options, active, -1),
  Home: () => 0,
  End: (options) => options.length - 1,
};

const isTypedLetter = (event: KeyboardEvent): boolean =>
  event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey;

export const SelectPopup = ({ id, label, options, value, place, onPick, onClose }: SelectPopupProps) => {
  const list = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(() =>
    Math.max(
      0,
      options.findIndex((option) => option.value === value),
    ),
  );
  const activeOption = options[active];

  useEffect(() => {
    list.current?.focus();
  }, []);

  useEffect(() => {
    list.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const pickActive = () => {
    if (activeOption !== undefined) onPick(activeOption.value);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const move = KEY_STEPS[event.key];
    if (move !== undefined) {
      event.preventDefault();
      setActive(move(options, active));
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      pickActive();
    } else if (event.key === 'Escape' || event.key === 'Tab') {
      event.preventDefault();
      onClose();
    } else if (isTypedLetter(event)) setActive(typedMatch(options, active, event.key));
  };

  return (
    <ul
      ref={list}
      id={id}
      role="listbox"
      tabIndex={-1}
      aria-label={label}
      aria-activedescendant={activeOption === undefined ? undefined : `${id}-${String(active)}`}
      className={styles['popup']}
      style={{ ...place, width: undefined, minWidth: place.width }}
      onKeyDown={onKeyDown}
    >
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          <li
            key={option.value}
            id={`${id}-${String(index)}`}
            role="option"
            aria-selected={selected}
            data-active={index === active}
            className={styles['option']}
            onPointerMove={() => {
              setActive(index);
            }}
            onClick={() => {
              onPick(option.value);
            }}
          >
            <span className={styles['optionLabel']}>{option.label}</span>
            {selected ? <Check size={16} aria-hidden="true" className={styles['optionCheck']} /> : null}
          </li>
        );
      })}
    </ul>
  );
};
